/**
 * UserProfileHeader — strip at the top of the drill-down page (IA §8).
 *
 * Layout (left → right):
 *   - Username (large) with Slack ID copy-to-clipboard affordance below
 *   - Role badge (RM green / non-RM grey / unknown outline) — mirrors the
 *     badge palette used by QualityPage/EngagementPage tables for
 *     cross-surface consistency
 *   - Cohort chip (power=purple, engaged=blue, active=grey, null=none)
 *   - First active date (formatted short)
 *   - Last active relative time ("2 h ago")
 *   - Total requests (lifetime)
 *   - Voice share % (lifetime)
 *
 * All fields are lifetime-scoped — the FilterBar date range does NOT apply
 * here because the profile endpoint is time-independent (Plan 04-04 truth).
 */

import { useState } from 'react';

import { BookOpen, Database, Ticket, type LucideIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDateMedium, formatRelativeCet } from '@/lib/format-date';
import { cn } from '@/lib/utils';
import type {
  AccessState,
  UserAccessFlag,
  UserProfile,
  UserSsoAccess,
} from '@/services/api-types';

// ---------------------------------------------------------------------------
// Relative time helper — kept inline (tiny; avoids pulling date-fns into the
// component for one formatter). Same rule of thumb as BriefStatusTable.
// ---------------------------------------------------------------------------

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  return formatRelativeCet(iso);
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  return formatDateMedium(iso);
}

// ---------------------------------------------------------------------------
// Role + cohort chips — match the palette used by the cross-tab tables so
// the same colour always means the same thing (IA §1.6 consistency rule).
// ---------------------------------------------------------------------------

function RoleBadge({
  isRm,
}: {
  isRm: UserProfile['isRm'];
}): React.ReactElement {
  if (isRm === true) {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600/80">
        RM
      </Badge>
    );
  }
  if (isRm === false) {
    return (
      <Badge variant="secondary" className="bg-blue-100 text-blue-900">
        Non-RM
      </Badge>
    );
  }
  return <Badge variant="outline">Unknown</Badge>;
}

const COHORT_CLASS: Record<'active' | 'engaged' | 'power', string> = {
  power: 'bg-purple-100 text-purple-900 border-purple-200',
  engaged: 'bg-blue-100 text-blue-900 border-blue-200',
  active: 'bg-gray-100 text-gray-700 border-gray-200',
};

// Mirror of `COHORT_DEFINITIONS` in `src/services/analytics/filters.ts` —
// kept inline so the tooltip renders synchronously without a fetch. Update
// these strings when the SSOT thresholds change (the FilterBar `labelHint`
// has the same obligation; both follow the same constants).
const COHORT_TOOLTIP: Record<
  'active' | 'engaged' | 'power',
  { label: string; description: string }
> = {
  active: {
    label: 'Active',
    description:
      '≥1 reactive request in the last 30 days (scheduled briefs excluded).',
  },
  engaged: {
    label: 'Engaged',
    description:
      '≥5 reactive requests across ≥3 distinct days in the last 30 days. Multiple touches per week.',
  },
  power: {
    label: 'Power',
    description:
      '≥15 reactive requests across ≥5 distinct days in the last 30 days. Near-daily driver.',
  },
};

function CohortChip({
  cohort,
}: {
  cohort: UserProfile['cohort'];
}): React.ReactElement | null {
  if (cohort === null) return null;
  const def = COHORT_TOOLTIP[cohort];
  // The shared `Tooltip` wrapper auto-swaps to Popover on touch devices
  // (see `components/ui/tooltip.tsx`), so the same markup gets hover on
  // desktop and tap-to-open on mobile without per-call branching.
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="cohort-chip"
            data-cohort={cohort}
            tabIndex={0}
            aria-label={`Cohort: ${def.label}. ${def.description}`}
            className={cn(
              'inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium cursor-help',
              COHORT_CLASS[cohort],
            )}
          >
            {cohort}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-0.5 text-left">
            <div className="font-medium">{def.label}</div>
            <p className="leading-snug">{def.description}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Access pills — SSO / Jira / Confluence. Three categorically independent
// credentials, each with its own state machine:
//
//   SSO  (AuthDB OAuth handshake) — 3-state from the OAuth token:
//     fresh   → token usable (not expired, or refreshable)         (green)
//     expired → access expired AND no refresh token (needs reauth) (red)
//     none    → no row, or access_token = 'PAT_ONLY' (disconnected) (outline)
//
//   Jira / Confluence — binary, since the project persists no per-PAT
//   freshness signal (validation only at /connect submit, not stored):
//     fresh   → PAT stored                                          (green)
//     none    → PAT absent                                          (outline)
//
// SSO state reflects OAuth connection health only (fresh/expired/none). RM
// status and role are shown by the RoleBadge (from `user_role_cache`); AuthDB
// role introspection was removed, so there is no role-freshness signal here.
//
// Truth source: `user_oauth_tokens` (provider='authdb') — see
// `computeUserProfile` + `buildAccessShape` in
// `src/services/analytics/queries.ts`.
// ---------------------------------------------------------------------------

const STATE_CLASS: Record<AccessState, string> = {
  fresh: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  expired: 'bg-red-100 text-red-900 border-red-200',
  none: 'bg-transparent text-gray-400 border-gray-200 [&_svg]:opacity-60',
};

const STATE_LABEL: Record<AccessState, string> = {
  fresh: 'connected',
  expired: 'needs reauth',
  none: 'not connected',
};

interface AccessPillProps {
  kind: 'sso' | 'jira' | 'confluence';
  label: string;
  Icon: LucideIcon;
  state: AccessState;
  /**
   * Lines rendered inside the tooltip body, top-to-bottom. Falsy entries are
   * dropped so callers can compose lines conditionally without an `if` step.
   */
  tooltipLines: Array<string | null | false>;
}

function AccessPill({
  kind,
  label,
  Icon,
  state,
  tooltipLines,
}: AccessPillProps): React.ReactElement {
  const lines = tooltipLines.filter(
    (line): line is string => typeof line === 'string' && line.length > 0,
  );
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`access-${kind}`}
            data-state={state}
            aria-label={`${label} access: ${STATE_LABEL[state]}`}
            className={cn(
              'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium cursor-default',
              STATE_CLASS[state],
            )}
          >
            <Icon className="h-3 w-3" aria-hidden="true" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5 text-left">
            {lines.length > 0 ? (
              lines.map((line) => <div key={line}>{line}</div>)
            ) : (
              <div>No access</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// SSO header lines per state — a small lookup so each state has one
// authoritative, hand-tuned phrasing rather than a templated string.
const SSO_HEADLINE: Record<AccessState, string> = {
  fresh: 'SSO connected',
  expired: 'SSO needs reconnect',
  none: 'SSO not connected',
};

function SsoPill({
  sso,
}: {
  sso: UserSsoAccess;
}): React.ReactElement {
  // The SSO pill reports only OAuth connection health. RM status and role are
  // shown by the RoleBadge (from user_role_cache); there is no AuthDB role
  // freshness to report anymore.
  const lines: Array<string | null | false> = [SSO_HEADLINE[sso.state]];
  return (
    <AccessPill
      kind="sso"
      label="SSO"
      Icon={Database}
      state={sso.state}
      tooltipLines={lines}
    />
  );
}

function PatPill({
  kind,
  label,
  Icon,
  flag,
}: {
  kind: 'jira' | 'confluence';
  label: string;
  Icon: LucideIcon;
  flag: UserAccessFlag;
}): React.ReactElement {
  // PATs are binary: present (`fresh`) or absent (`none`). No freshness
  // signal exists in the codebase (validation runs only at /connect submit),
  // so the tooltip stays terse and honest.
  const lines: Array<string | null | false> =
    flag.state === 'fresh'
      ? [`${label} PAT stored`]
      : [`${label} PAT not configured`];
  return (
    <AccessPill
      kind={kind}
      label={label}
      Icon={Icon}
      state={flag.state}
      tooltipLines={lines}
    />
  );
}

function AccessRow({
  access,
}: {
  access: UserProfile['access'];
}): React.ReactElement {
  return (
    <span
      data-testid="user-access-row"
      className="inline-flex items-center gap-1.5"
    >
      <SsoPill sso={access.sso} />
      <PatPill kind="jira" label="Jira" Icon={Ticket} flag={access.jira} />
      <PatPill
        kind="confluence"
        label="Confluence"
        Icon={BookOpen}
        flag={access.confluence}
      />
    </span>
  );
}

// ---------------------------------------------------------------------------
// SlackIdCopy — muted monospace row under the username; click copies the
// Slack ID to the clipboard. Silently no-ops when the API is missing.
// ---------------------------------------------------------------------------

function SlackIdCopy({ slackId }: { slackId: string }): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(slackId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }
    } catch {
      // Swallow — clipboard API may be unavailable (HTTP dev, sandbox).
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid="user-slack-id-copy"
      className="font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
      title="Click to copy Slack ID"
    >
      {slackId}
      {copied && <span className="ml-2 text-green-600">copied</span>}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PCT_FMT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 0,
});
const NUM_FMT = new Intl.NumberFormat('en-US');

export interface UserProfileHeaderProps {
  profile: UserProfile;
}

export function UserProfileHeader({
  profile,
}: UserProfileHeaderProps): React.ReactElement {
  return (
    <Card data-testid="user-profile-header">
      <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-4">
        {/* Left block — real name (when known) + handle + Slack ID copy */}
        <div className="flex-1 min-w-0">
          <h1
            data-testid="user-header-name"
            className="text-2xl font-semibold leading-tight truncate"
          >
            {profile.realName && profile.realName.trim().length > 0
              ? profile.realName
              : '—'}
          </h1>
          <div
            data-testid="user-header-handle"
            className="text-sm text-muted-foreground truncate"
          >
            @{profile.userId}
          </div>
          {profile.slackId && (
            <div className="mt-1">
              <SlackIdCopy slackId={profile.slackId} />
            </div>
          )}
        </div>

        {/* Right block — badges + metrics row */}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <RoleBadge isRm={profile.isRm} />
          <CohortChip cohort={profile.cohort} />
          <AccessRow access={profile.access} />
          <span
            data-testid="user-first-active"
            className="text-muted-foreground"
          >
            First: {shortDate(profile.firstActive)}
          </span>
          <span
            data-testid="user-last-active"
            className="text-muted-foreground"
          >
            Last: {relativeTime(profile.lastActive)}
          </span>
          <span data-testid="user-total-requests" className="tabular-nums">
            <span className="font-medium">
              {NUM_FMT.format(profile.totalRequests)}
            </span>
            <span className="ml-1 text-muted-foreground">requests</span>
          </span>
          <span data-testid="user-voice-share" className="tabular-nums">
            <span className="font-medium">
              {PCT_FMT.format(profile.voiceShare)}
            </span>
            <span className="ml-1 text-muted-foreground">voice</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
